import React, { useState, useEffect } from "react";
import { FaStar, FaQuoteLeft } from "react-icons/fa";
import { Link } from "react-router-dom";
import Reveal from "../../components/Reveal";
import { Helmet } from "react-helmet-async";
import axiosInstance from "../../api/axiosInstance";

/* ─── Static fallback testimonials (used when DB is empty) ─────── */
const STATIC_TESTIMONIALS = [
  {
    id: 1,
    name: "Mansi Patel",
    role: "Business Owner",
    company: "",
    message:
      "Very good IT company in Dubai. Helpful team and great service. I personally recommend them.",
    rating: 4,
  },
  {
    id: 2,
    name: "Simran Singh",
    role: "Entrepreneur",
    company: "",
    message:
      "Best platform to grow your business through Novarsis Tech. Best services & experienced team.",
    rating: 5,
  },
  {
    id: 3,
    name: "Khushi Tiwari",
    role: "Startup Founder",
    company: "",
    message:
      "Outstanding service from Novarsis Tech and one of the best in the company. 👌",
    rating: 5,
  },
  {
    id: 4,
    name: "Monika Singh",
    role: "Marketing Head",
    company: "",
    message:
      "Amazing development team, always on time and very professional. Highly recommended!",
    rating: 5,
  },
  {
    id: 5,
    name: "Anya Agrawal",
    role: "Project Manager",
    company: "",
    message:
      "Working with Novarsis was a great experience. The team is highly skilled and dedicated.",
    rating: 4,
  },
  {
    id: 6,
    name: "Ravi Patel",
    role: "CEO",
    company: "",
    message:
      "Their innovative ideas and attention to detail helped us boost our business growth.",
    rating: 5,
  },
];

const TestimonialsSection = () => {
  const [backendTestimonials, setBackendTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  // Fetch from backend
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axiosInstance.get("/testimonials");
        const data = response.data?.data || [];
        setBackendTestimonials(data);
      } catch (err) {
        console.error("Failed to fetch testimonials:", err);
        // On error stays empty → falls back to static
      } finally {
        setLoadingTestimonials(false);
      }
    };
    fetchTestimonials();
  }, []);

  // Core logic: prefer DB, fallback to static
  const displayedTestimonials =
    backendTestimonials.length > 0 ? backendTestimonials : STATIC_TESTIMONIALS;

  /* ─── Structured data (updates with live data) ─────── */
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://novarsistech.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Testimonials",
        item: "https://novarsistech.com/testimonials",
      },
    ],
  };

  return (
    <>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <Helmet>
        <title>
          Novarsis Technology | Leading IT Services & Software Development Company
        </title>

        <meta
          name="description"
          content="Read authentic client testimonials and reviews for Novarsis Technology. Discover why businesses trust us for web development, SEO, digital marketing, and IT solutions."
        />

        <meta
          name="keywords"
          content="Client testimonials, Novarsis Technology reviews, IT services reviews, web development testimonials"
        />

        <link rel="canonical" href="https://novarsistech.com/testimonials" />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <nav aria-label="Breadcrumb" className="sr-only">
        <Link to="/">Home</Link> / Testimonials
      </nav>

      <article className="bg-white">
        <section className="relative py-28 px-5 sm:px-8 overflow-hidden">
          <h2 className="sr-only">
            Client Testimonials and Reviews for Novarsis Technology IT Services
          </h2>

          {/* Background */}
          <div
            className="absolute inset-0 bg-cover bg-center scale-105"
            style={{ backgroundImage: "url('/Images/parallex10.jpg')" }}
          />
          <div className="absolute inset-0 bg-black/65" />

          <div className="relative z-10 max-w-7xl mx-auto">
            {/* Heading */}
            <div className="text-center max-w-3xl mx-auto">
              <Reveal>
                <h4 className="text-[#008300] font-extrabold uppercase tracking-widest text-sm">
                  What Our Clients Say
                </h4>
              </Reveal>

              <Reveal>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mt-3 text-white leading-tight">
                  Client Testimonials & Success Stories
                </h1>
              </Reveal>

              <Reveal>
                <p className="text-gray-300 mt-5 text-base sm:text-lg leading-relaxed">
                  Real experiences from businesses across industries who
                  partnered with Novarsis Technology to build powerful digital
                  solutions and achieve long-term growth.
                </p>
              </Reveal>
            </div>

            {/* Cards */}
            <div className="mt-16 md:mt-24 -mx-5 sm:-mx-8">
              {/* Skeleton while loading */}
              {loadingTestimonials && (
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 px-5 sm:px-8">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-56 bg-white/10 rounded-2xl animate-pulse"
                    />
                  ))}
                </div>
              )}

              {/* Testimonial cards */}
              {!loadingTestimonials && (
                <div className="flex gap-5 overflow-x-auto hide-scrollbar pt-4 pb-6 snap-x snap-mandatory px-5 sm:px-8">
                  {displayedTestimonials.map((t, index) => (
                    <Reveal key={t.id || index} className="h-full flex-shrink-0 w-80 snap-start">
                      <div
                        itemScope
                        itemType="https://schema.org/Review"
                        className="w-full h-full bg-white/95 backdrop-blur-xl rounded-2xl p-6 text-black shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[#008300]/30 flex flex-col"
                      >
                        {/* Quote icon — replaces old avatar image */}
                        <div className="flex justify-center mb-5">
                          <div className="w-14 h-14 rounded-full bg-[#008300]/10 border-2 border-[#008300]/30 flex items-center justify-center">
                            <FaQuoteLeft className="text-2xl text-[#008300]" />
                          </div>
                        </div>

                        {/* Message */}
                        <p
                          itemProp="reviewBody"
                          className="text-center italic text-gray-700 leading-relaxed mb-5 text-sm sm:text-base flex-1"
                        >
                          "{t.message || t.review}"
                        </p>

                        {/* Name */}
                        <h3
                          itemProp="author"
                          className="text-center font-bold text-gray-900 text-base"
                        >
                          {t.name}
                        </h3>

                        {/* Role / Company */}
                        {(t.role || t.company) && (
                          <p className="text-center text-xs text-gray-500 mt-0.5">
                            {t.role}
                            {t.role && t.company && " · "}
                            {t.company && (
                              <span className="text-[#008300] font-medium">
                                {t.company}
                              </span>
                            )}
                          </p>
                        )}

                        {/* Star Rating */}
                        {t.rating && (
                          <div
                            className="flex justify-center mt-4 gap-0.5"
                            itemProp="reviewRating"
                            itemScope
                            itemType="https://schema.org/Rating"
                          >
                            {[...Array(5)].map((_, i) => (
                              <FaStar
                                key={i}
                                className={`text-base ${
                                  i < t.rating
                                    ? "text-yellow-400"
                                    : "text-gray-200"
                                }`}
                              />
                            ))}
                            <span itemProp="ratingValue" className="sr-only">
                              {t.rating}
                            </span>
                          </div>
                        )}
                      </div>
                    </Reveal>
                  ))}
                </div>
              )}
            </div>

            {/* Scroll hint — desktop only */}
            <Reveal>
              <p className="hidden lg:block text-center text-gray-400 mt-6 text-sm">
                👉 Scroll to explore more client stories
              </p>
            </Reveal>

            {/* CTA */}
            <div className="mt-20 text-center">
              <p className="text-gray-300 text-base sm:text-lg mb-6">
                Interested in transforming your business like these successful
                clients?
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/project"
                  className="bg-[#008300] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#009e3c] transition"
                >
                  Explore Our Projects
                </Link>

                <Link
                  to="/contact"
                  className="border-2 border-[#008300] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#008300]/20 transition"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default TestimonialsSection;
